// The first screenful: is anything wrong, and is anything waiting on me.
//
// Counted server-side over the last 24 hours, so a capped page of knocks below can never make
// the headline wrong. Two things read as loud rather than as numbers — a failing job and a
// knock the write guard held back — because both mean somebody has to go and look.

import { Link } from 'react-router-dom';
import type { AdminOverviewPayload, SpendMeter } from '../api/types';
import { formatCount } from '../lib/format';
import { RUN_STATE_LABEL, RUN_STATE_TONE, briefsDegradedCount, formatStamp, runState } from './format.ts';
import { StatePill } from './AdminSection';

// What the waiting tile says under its number. The split matters more than the total: a
// morning proposal can sit until tomorrow, a strategist's request is somebody waiting.
function queueFoot(overview: AdminOverviewPayload): string {
  const requested = overview.queue.request_count;
  const proposed = overview.queue.pick_count - requested;
  if (overview.queue.pick_count === 0) return 'nothing pending';
  const parts: string[] = [];
  if (requested > 0) {
    parts.push(`${formatCount(requested)} requested by a strategist`);
  }
  if (proposed > 0) parts.push(`${formatCount(proposed)} from the morning top-up`);
  return parts.join(' · ');
}

function Tile({
  label,
  value,
  foot,
  tone,
  to,
}: {
  label: string;
  value: string;
  foot: string;
  tone?: 'ok' | 'warn' | 'bad';
  to?: string;
}) {
  const valueClass = tone ? 'val ' + tone : 'val';
  const className = tone ? 'tile ' + tone : 'tile';
  const body = (
    <>
      <div className="lab">{label}</div>
      <div className={valueClass}>{value}</div>
      <div className="foot">{foot}</div>
    </>
  );
  if (to) return <Link to={to} className={className}>{body}</Link>;
  return <div className={className}>{body}</div>;
}

const CREDIT_METER_LABEL: Record<SpendMeter['meter'], string> = {
  usd: "Today's vendor budget",
  clay_credit: 'Clay credits today',
  apollo_credit: 'Apollo credits today',
};

// Dry is bad rather than warn: every call on that meter is being refused right now.
function meterTone(meter: SpendMeter): 'warn' | 'bad' | undefined {
  if (!meter.readable) return 'warn';
  if (meter.exhausted) return 'bad';
  return undefined;
}

export function StatusStrip({ overview }: { overview: AdminOverviewPayload | undefined }) {
  if (!overview) return null;

  const failing = overview.jobs.failing;
  const briefsDegraded = briefsDegradedCount(overview.jobs.latest);
  const unwritten = overview.knocks.unwritten;
  const waiting = overview.queue.pick_count;
  // A strategist asking for an account is a person waiting on an answer; a morning proposal
  // is not. The tile leads with the requests for that reason, and never sums the two.
  const requested = overview.queue.request_count;
  const briefCount = overview.briefs.count;

  // An unreadable meter must not render as a healthy one. "unknown" is the honest value:
  // not seeing the budget is exactly what an exhausted budget looks like from outside.
  const daily = overview.spend.daily;
  const dailyBudgetValue = daily.readable ? '$' + daily.remaining : 'unknown';
  const dailyBudgetFoot = daily.readable
    ? `$${daily.spent} spent · $${daily.held} held · of $${daily.limit} today`
    : 'the daily meter could not be read';
  const dailyBudgetTone = meterTone(daily);

  return (
    <>
      {/* Loudest thing on the page, and only ever present when a cron came back non-zero or
          never came back at all. A count in a column would be read once a week; this is read
          on the way past. */}
      {failing.length > 0 ? (
        <div className="adminalarm" role="status">
          <b>{failing.length === 1 ? 'One job' : `${failing.length} jobs`} need looking at.</b>
          <ul>
            {failing.map(run => (
              <li key={run.run_id}>
                <code>{run.job_name}</code> — {RUN_STATE_LABEL[runState(run)]} at{' '}
                {formatStamp(run.started_at)}
                {run.failed_count > 0 ? ` · ${formatCount(run.failed_count)} failed` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* A degraded brief run exits 0, so the failing-jobs alarm never shows it; this does. */}
      {briefsDegraded > 0 ? (
        <div className="adminalarm adminwarn" role="status">
          <b>Briefs degraded:</b> {briefsDegraded === 1 ? '1 strategist' : `${formatCount(briefsDegraded)} strategists`} got
          facts-only notes on the latest <code>write_briefs</code> run.
        </div>
      ) : null}

      <div className="strip adminstrip">
        <Tile
          label="Jobs"
          value={failing.length === 0 ? 'All green' : formatCount(failing.length)}
          foot={
            overview.jobs.latest.length === 0
              ? 'no run has been recorded yet'
              : `${formatCount(overview.jobs.latest.length)} jobs have run`
          }
          tone={failing.length === 0 ? 'ok' : 'bad'}
        />
        <Tile
          label="Waiting on you"
          value={formatCount(waiting)}
          foot={queueFoot(overview)}
          // A strategist's request is somebody blocked on a person, so it is warned about
          // even when a morning proposal beside it would not be.
          tone={requested > 0 ? 'bad' : waiting > 0 ? 'warn' : undefined}
          to="/available#proposals"
        />
        <Tile
          label="Knocks"
          value={formatCount(overview.knocks.total)}
          foot={`in the last ${overview.window_hours} hours`}
        />
        <Tile
          label="Held back"
          value={formatCount(unwritten)}
          foot="nothing reached Salesforce for these"
          tone={unwritten > 0 ? 'warn' : undefined}
        />
        <Tile
          label="Briefs"
          value={formatCount(briefCount)}
          foot={
            overview.briefs.brief_on
              ? `written for ${overview.briefs.brief_on}`
              : 'no brief has been written'
          }
          tone={briefCount === 0 ? 'warn' : undefined}
        />
        <Tile
          label="Enrichment spend"
          value={'$' + overview.spend.usd}
          // Deliberately not "budget remaining". SCOUT_DAILY_ENRICHMENT_BUDGET_USD is a
          // *per-run* cap, so two runs in a day spend it twice and a remaining figure would be
          // wrong by however many times the ladder ran.
          foot={`last ${overview.window_hours}h · $${overview.spend.per_run_cap_usd} cap per run`}
        />
        {/* The day's shared pool, which the per-run cap above is NOT. When this is dry every
            interactive call is refused — Scout says the budget is exhausted and, before this
            tile existed, the only way to confirm it was the Render log. */}
        <Tile
          label="Today's vendor budget"
          value={dailyBudgetValue}
          foot={dailyBudgetFoot}
          tone={dailyBudgetTone}
        />
        {/* Clay and Apollo bill in their own credits, so each has its own daily limit and its
            own tile. A dry credit meter holds that vendor back until the Pacific day turns. */}
        {overview.spend.credits.map(meter => (
          <Tile
            key={meter.meter}
            label={CREDIT_METER_LABEL[meter.meter]}
            value={meter.readable ? `${meter.remaining} left` : 'unknown'}
            foot={
              meter.readable
                ? `${meter.spent} spent · ${meter.held} held · of ${meter.limit} today`
                : 'the credit meter could not be read'
            }
            tone={meterTone(meter)}
          />
        ))}
      </div>

      {/* One line naming what the outcomes were, under the counts rather than as more
          tiles. The breakdown is the CHECK on intake_events.outcome, not a scoreboard. */}
      <p className="adminbreakdown">
        {Object.keys(overview.knocks.by_outcome).length === 0
          ? 'No knock has been recorded in this window.'
          : Object.entries(overview.knocks.by_outcome)
              .map(([outcome, count]) => `${formatCount(count)} ${outcome}`)
              .join(' · ')}
        {overview.jobs.latest.length > 0 ? (
          <>
            {' — '}
            {overview.jobs.latest.map(run => (
              <span key={run.run_id} className="adminjobchip">
                <code>{run.job_name}</code>
                <StatePill tone={RUN_STATE_TONE[runState(run)]} label={formatStamp(run.started_at)} />
              </span>
            ))}
          </>
        ) : null}
      </p>
    </>
  );
}
